import { useState } from 'react';
import { AppBar, Box, Drawer, IconButton, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme/tokens';
import { useAuth, useLogout } from '../lib/auth';
import { api } from '../lib/api-client';
// ImpersonationBanner + useImpersonationActivate del legacy NO portados — feature requiere
// backend wiring específico (recruiter impersonando cliente) que no está implementado en el
// monorepo. Tracked en SW-128 follow-up.
import { TenantBrand } from '../tenant/TenantBrand';
import { LocaleSwitcher } from './LocaleSwitcher';

/**
 * Layout principal del portal con sidebar responsive (SW-136):
 *  - Desktop (md+): drawer `permanent` 248px siempre visible, contenido con ml=sidebar.
 *  - Mobile (xs/sm): drawer `temporary` que sale por hamburger button en un AppBar top.
 *    Se auto-cierra al navegar y al tap fuera.
 *
 * Tenant brand + nav items + logout. Estructura matchea el handoff del design
 * (portal-shell.css) sin pretender ser pixel-perfect todavía.
 *
 * `data-testid` se mantiene en elementos clave (sidebar, nav items, logout) para que los
 * tests E2E (Playwright) puedan localizar sin acoplarse a texto traducido.
 */
export function PortalLayout() {
  useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const sidebarWidth = `${tokens.size.sidebarWidth}px`;
  const [mobileOpen, setMobileOpen] = useState(false);

  const logout = useLogout({
    apiClient: api,
    onSuccess: () => navigate('/login', { replace: true }),
  });

  function handleLogout() {
    logout.mutate();
  }

  function handleNavClick() {
    if (!isDesktop) setMobileOpen(false);
  }

  // Cierra el drawer cuando cambia la ruta en mobile (cubre navegaciones que no pasan
  // por NavItem — ej. redirects programáticos).
  if (mobileOpen && isDesktop) {
    // Hard-close si el browser pasa de mobile → desktop con drawer abierto.
    setMobileOpen(false);
  }

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        bgcolor: 'background.paper',
        borderRight: { md: `1px solid ${tokens.color.line}` },
        display: 'flex',
        flexDirection: 'column',
        p: '22px 14px 18px',
        fontFamily: tokens.font.display,
      }}
    >
      <Box
        sx={{
          pb: 2.5,
          mb: 2,
          borderBottom: `1px solid ${tokens.color.line}`,
          px: 1,
        }}
      >
        <TenantBrand size="sidebar" />
      </Box>

      <Box
        component="nav"
        data-testid="portal-nav"
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1 }}
      >
        <NavItem
          to="/opportunities"
          label={t('app.nav.opportunities')}
          testId="nav-opportunities"
          onClick={handleNavClick}
        />
        <NavItem
          to="/profile"
          label={t('app.nav.profile')}
          testId="nav-profile"
          onClick={handleNavClick}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          pt: 1.5,
          mt: 'auto',
          borderTop: `1px solid ${tokens.color.line}`,
        }}
      >
        <Box sx={{ px: '12px', py: '6px' }}>
          <LocaleSwitcher variant="sidebar" />
        </Box>
        <Box
          component="button"
          onClick={handleLogout}
          data-testid="nav-logout"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            p: '9px 12px',
            borderRadius: '9px',
            border: 0,
            background: 'transparent',
            color: tokens.color.muted,
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            textAlign: 'left',
            '&:hover': { background: tokens.color.warnSoft, color: tokens.color.warn },
          }}
        >
          {t('app.nav.logout')}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* AppBar top — solo mobile, expone hamburger para abrir el drawer. */}
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          display: { xs: 'flex', md: 'none' },
          borderBottom: `1px solid ${tokens.color.line}`,
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar sx={{ minHeight: 56, gap: 1.5 }}>
          <IconButton
            edge="start"
            data-testid="portal-mobile-menu-toggle"
            aria-label={t('app.nav.openMenu')}
            onClick={() => setMobileOpen(true)}
            sx={{ color: tokens.color.ink }}
          >
            <HamburgerIcon />
          </IconButton>
          <TenantBrand size="sidebar" />
        </Toolbar>
      </AppBar>

      {/* Desktop: drawer permanent. Mobile: temporary con backdrop. */}
      <Box
        component="nav"
        data-testid="portal-sidebar"
        sx={{ width: { md: sidebarWidth }, flexShrink: 0 }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: sidebarWidth, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              borderRight: `1px solid ${tokens.color.line}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main */}
      <Box
        component="main"
        data-testid="portal-main"
        // pt extra en mobile compensa el AppBar fixed; en desktop el sidebar es flex sibling.
        sx={{
          flex: 1,
          minWidth: 0,
          maxWidth: `${tokens.size.shellMaxWidth}px`,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: '72px', md: 3 },
          pb: 10,
        }}
        key={location.pathname}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

function NavItem({
  to,
  label,
  testId,
  onClick,
}: {
  to: string;
  label: string;
  testId: string;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      onClick={onClick}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '9px 12px',
        borderRadius: 9,
        textDecoration: 'none',
        color: isActive ? tokens.color.ink : tokens.color.ink2,
        fontSize: 13.5,
        fontWeight: isActive ? 600 : 500,
        background: isActive ? tokens.color.surface2 : 'transparent',
      })}
    >
      {label}
    </NavLink>
  );
}

// Hamburger inline (evita import @mui/icons-material si no está en deps).
function HamburgerIcon() {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{ width: 24, height: 24, display: 'block' }}
      aria-hidden
    >
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </Box>
  );
}
